import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useLocale, useCurrency } from "../../intl";
import { localeToUrlSegment } from "../../intl/manifestI18n";
import { getI18nRollout } from "../../intl/i18nRollout.js";
import { useClientViewRouteLocale } from "../context/ClientViewRouteLocaleContext";
import { writeClientViewLocaleCookie } from "../../intl/clientViewPreferences.js";
import { FX_CURRENCY_ANALYTICS_SURFACES, FX_CURRENCY_SOURCE_COMPONENTS } from "../../constants/fxCurrencyAnalyticsConstants.js";
import { useLocalizedRuntimePaths } from "../hooks/useLocalizedRuntimePaths";
import { usePublicAccount } from "../context/PublicAccountProvider";
import { runtimeAccountPath } from "../utils/routes";
import { useClientView } from "../context/ClientViewProvider";

const LOCALE_LABELS = {
  "es-CO": "ES",
  "en-US": "EN",
  "es-MX": "ES",
  "pt-BR": "PT",
  "fr-FR": "FR",
  "ru-RU": "RU",
};

function AutoMapaLocalePillSelect({
  id,
  label,
  value,
  options,
  onChange,
  onOpenChange,
  isOpen,
}) {
  const selected = options.find((option) => option.value === value);

  return (
    <div className={`automap-locale-pill${isOpen ? " is-open" : ""}`}>
      <button
        type="button"
        id={id}
        className="automap-locale-pill__trigger control"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        onClick={() => onOpenChange(!isOpen)}
      >
        <span className="automap-locale-pill__label">{label}</span>
        <span className="automap-locale-pill__value">{selected?.label || value}</span>
        <span className="automap-locale-pill__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      {isOpen ? (
        <ul className="automap-locale-pill__menu" role="listbox" aria-labelledby={id}>
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={option.value === value ? "is-selected" : undefined}
                onClick={() => {
                  onChange(option.value);
                  onOpenChange(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function AutoMapaLocaleControls({
  className = "nav-right",
  searchAction = null,
  showSignIn = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug, isPreview, manifest } = useClientView();
  const { buildLocaleSwitchPath, urlLocale, manifestI18n } = useClientViewRouteLocale();
  const rollout = useMemo(
    () => getI18nRollout({ i18n: manifestI18n }),
    [manifestI18n]
  );
  const { setLocale, supportedLocales, t } = useLocale();
  const { displayCurrency, setDisplayCurrency, supportedCurrencies } = useCurrency();
  const { signInPath } = useLocalizedRuntimePaths();
  const { isAuthenticated } = usePublicAccount();
  const activeLocale = urlLocale ?? manifestI18n.defaultLocale;
  const containerRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);

  const handleLocaleChange = (nextLocale) => {
    writeClientViewLocaleCookie(nextLocale);
    const nextPath = buildLocaleSwitchPath(nextLocale);
    if (nextPath) {
      const currentPath = `${location.pathname}${location.search}`;
      if (nextPath !== currentPath) {
        navigate(nextPath);
      }
      return;
    }
    setLocale(nextLocale);
  };

  const localeChoices =
    supportedLocales.length > 1 ? supportedLocales : manifestI18n.supportedLocales;

  const localeOptions =
    localeChoices.length > 1 && rollout.clientViewI18nEnabled !== false
      ? localeChoices.map((value) => ({
          value,
          label: LOCALE_LABELS[value] || localeToUrlSegment(value)?.toUpperCase() || value,
        }))
      : [];

  const currencyChoices =
    supportedCurrencies.length > 1
      ? supportedCurrencies
      : manifestI18n.supportedCurrencies;

  const currencyOptions =
    currencyChoices.length > 1 && rollout.displayCurrencyEnabled !== false
      ? currencyChoices.map((option) => ({ value: option, label: option }))
      : [];

  const showLanguage = localeOptions.length >= 2;
  const showCurrency = currencyOptions.length >= 2;

  useEffect(() => {
    if (!openMenu) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu]);

  if (!showLanguage && !showCurrency && !searchAction && !showSignIn) {
    return null;
  }

  const accountHref = isAuthenticated
    ? runtimeAccountPath(slug, activeLocale, manifest)
    : signInPath;
  const accountLabel = isAuthenticated
    ? t("automotive.nav.account")
    : t("automotive.nav.signIn");

  return (
    <div
      ref={containerRef}
      className={`${className} automap-locale-controls`.trim()}
    >
      {showLanguage ? (
        <AutoMapaLocalePillSelect
          id="automap-language-select"
          label={t("common.language")}
          value={activeLocale}
          options={localeOptions}
          onChange={handleLocaleChange}
          isOpen={openMenu === "language"}
          onOpenChange={(nextOpen) => setOpenMenu(nextOpen ? "language" : null)}
        />
      ) : null}
      {showCurrency ? (
        <AutoMapaLocalePillSelect
          id="automap-currency-select"
          label={t("common.currency")}
          value={displayCurrency}
          options={currencyOptions}
          onChange={(nextCurrency) =>
            setDisplayCurrency(nextCurrency, {
              sourceComponent: FX_CURRENCY_SOURCE_COMPONENTS.LOCALE_CONTROLS,
              surface: FX_CURRENCY_ANALYTICS_SURFACES.CURRENCY_CHANGE,
            })
          }
          isOpen={openMenu === "currency"}
          onOpenChange={(nextOpen) => setOpenMenu(nextOpen ? "currency" : null)}
        />
      ) : null}
      {showSignIn && !isPreview ? (
        <Link to={accountHref} className="btn btn-primary automap-nav-signin">
          {accountLabel}
        </Link>
      ) : null}
      {searchAction}
    </div>
  );
}
